import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Pie, Line } from 'react-chartjs-2';
import '../assets/Overview.css';
import { fetchData, fetchOverviewData, fetchCollectorStatistics } from '../api/restapi';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

function Overview({ userInfo, role }) {
  const [overviewData, setOverviewData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('oem');
  const [timePeriod, setTimePeriod] = useState('week');
  const [collectorStats, setCollectorStats] = useState(null);
  const [collectorStatsLoading, setCollectorStatsLoading] = useState(false);
  
  // SSE-related state
  const [eventSource, setEventSource] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [useSSE, setUseSSE] = useState(true); // Toggle for SSE vs regular API


  // Simple loading state - SSE will provide all data
  useEffect(() => {
    if (!userInfo) {
      setError('User information is required');
      setLoading(false);
      return;
    }
    
    // Keep loading state until SSE provides first data
    console.log('Waiting for SSE to provide initial data...');
  }, [userInfo]);

  // SSE connection for real-time updates
  useEffect(() => {
    if (!userInfo || !useSSE) return;

    const setupSSEConnection = () => {
      try {
        // Real SSE endpoint - your backend is ready!
        const sseUrl = `${process.env.REACT_APP_WBC_API_BASE_URL}/api/dashboard/stream?userEmail=${userInfo}`;
        
        console.log('SSE: Connecting to', sseUrl);
        const es = new EventSource(sseUrl);
        
        es.onopen = () => {
          console.log('SSE connection opened successfully');
          setIsConnected(true);
          setError(null);
        };
        
        // Create a shared handler for dashboard updates
        const handleDashboardUpdate = (event) => {
          try {
            const updatedData = JSON.parse(event.data);
            setOverviewData(updatedData);
            setLastUpdated(new Date());
            setLoading(false); // Stop loading when first data arrives
            console.log('SSE: Dashboard data updated from server (event type: ' + event.type + ')', updatedData);
          } catch (err) {
            console.error('SSE: Error parsing dashboard data:', err);
          }
        };

        // Listen for both 'dashboard-update' and 'dashboard-data' events
        es.addEventListener('dashboard-update', handleDashboardUpdate);
        es.addEventListener('dashboard-data', handleDashboardUpdate);
        
        es.onerror = (error) => {
          console.log('SSE connection event:', error.target.readyState);
          setIsConnected(false);
          
          // Don't show error messages for normal reconnection behavior
          // Only log to console for debugging
          if (error.target.readyState === EventSource.CLOSED) {
            console.log('SSE connection closed - will auto-reconnect');
          } else if (error.target.readyState === EventSource.CONNECTING) {
            console.log('SSE reconnecting...');
          }
          
          // Don't set error state for normal SSE reconnection
          // This prevents the disruptive "Error loading dashboard" message
        };
        
        setEventSource(es);
        
        return () => {
          console.log('SSE: Closing connection');
          es.close();
          setEventSource(null);
          setIsConnected(false);
        };
        
      } catch (err) {
        console.error('Failed to setup SSE connection:', err);
        setError('Failed to establish real-time connection');
        setIsConnected(false);
      }
    };

    const cleanup = setupSSEConnection();
    
    return cleanup;
  }, [userInfo, useSSE]);

  // Cleanup SSE connection on component unmount
  useEffect(() => {
    return () => {
      if (eventSource) {
        eventSource.close();
        setEventSource(null);
        setIsConnected(false);
      }
    };
  }, [eventSource]);

  const formatNumber = (num) => {
    return num.toLocaleString();
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '—';
    const kb = bytes / 1024;
    const mb = kb / 1024;
    const gb = mb / 1024;
    
    if (gb >= 1) return `${gb.toFixed(2)} GB`;
    if (mb >= 1) return `${mb.toFixed(2)} MB`;
    return `${kb.toFixed(2)} KB`;
  };

  // Fetch collector statistics when timePeriod changes
  useEffect(() => {
    if (overviewData) {
      setCollectorStatsLoading(true);
      const duration = timePeriod === 'week' ? 'week' : 'month';
      
      fetchCollectorStatistics(duration)
        .then(data => {
          if (data.success) {
            setCollectorStats(data.collectors);
          } else {
            console.error('Failed to fetch collector statistics:', data.message);
            setCollectorStats(null);
          }
        })
        .catch(err => {
          console.error('Error fetching collector statistics:', err);
          setCollectorStats(null);
        })
        .finally(() => {
          setCollectorStatsLoading(false);
        });
    }
  }, [timePeriod, overviewData]);

  const calculateSuccessRate = () => {
    if (!overviewData) return { successRate: 0, failureRate: 0 };
    
    // Filter data based on selected time period (same as chart)
    const allTrendData = overviewData.uploadStats.dailyTrend;
    let trendData;
    
    switch (timePeriod) {
      case 'week':
        trendData = allTrendData.slice(-7); // Last 7 days
        break;
      case 'month':
        trendData = allTrendData; // All 30 days
        break;
      default:
        trendData = allTrendData.slice(-7); // Default to week (7 days)
    }
    
    // Calculate totals from filtered data
    const totalSuccess = trendData.reduce((sum, item) => sum + item.success, 0);
    const totalFailure = trendData.reduce((sum, item) => sum + item.failure, 0);
    const total = totalSuccess + totalFailure;
    
    if (total === 0) return { successRate: 0, failureRate: 0 };
    
    return {
      successRate: ((totalSuccess / total) * 100).toFixed(0),
      failureRate: ((totalFailure / total) * 100).toFixed(0)
    };
  };

  const getPieChartData = () => {
    if (!overviewData) return null;
    
    const categoryData = overviewData.deviceSplit[selectedCategory];
    const labels = Object.keys(categoryData);
    const data = Object.values(categoryData);
    const colors = ['#3498db', '#2ecc71', '#f1c40f', '#e74c3c', '#9b59b6'];
    
    return {
      labels,
      datasets: [{
        data,
        backgroundColor: colors.slice(0, labels.length),
        borderWidth: 1,
        borderColor: '#fff'
      }]
    };
  };

  const getLineChartData = () => {
    if (!overviewData) return null;
    
    // Filter data based on selected time period
    const allTrendData = overviewData.uploadStats.dailyTrend;
    let trendData;
    
    switch (timePeriod) {
      case 'week':
        trendData = allTrendData.slice(-7); // Last 7 days
        break;
      case 'month':
        trendData = allTrendData; // All 30 days
        break;
      default:
        trendData = allTrendData.slice(-7); // Default to week (7 days)
    }
    
    const labels = trendData.map(item => {
      const date = new Date(item.date);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    });
    
    const successRates = trendData.map(item => {
      const total = item.success + item.failure;
      return total > 0 ? ((item.success / total) * 100).toFixed(1) : 0;
    });
    
    const failureRates = trendData.map(item => {
      const total = item.success + item.failure;
      return total > 0 ? ((item.failure / total) * 100).toFixed(1) : 0;
    });
    
    return {
      labels,
      datasets: [
        {
          label: 'Success Rate',
          data: successRates,
          borderColor: '#2ecc71',
          backgroundColor: 'rgba(46, 204, 113, 0.1)',
          fill: true,
          tension: 0.4
        },
        {
          label: 'Failure Rate',
          data: failureRates,
          borderColor: '#e74c3c',
          backgroundColor: 'rgba(231, 76, 60, 0.1)',
          fill: true,
          tension: 0.4
        }
      ]
    };
  };

  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const total = context.dataset.data.reduce((acc, val) => acc + val, 0);
            const value = context.raw;
            const percentage = Math.round((value / total) * 100);
            return `${context.label}: ${value} (${percentage}%)`;
          }
        }
      }
    }
  };

  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        ticks: {
          callback: function(value) {
            return value + '%';
          }
        }
      }
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          usePointStyle: true,
          pointStyle: 'line',
          pointStyleWidth: 20,
          padding: 20,
          font: {
            size: 12
          }
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return context.dataset.label + ': ' + context.raw + '%';
          }
        }
      }
    }
  };

  if (loading) {
    return (
      <div className="overview-container">
        <div className="overview-loading">
          <div className="loading-spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="overview-container">
        <div className="overview-error">
          <p>Error loading dashboard: {error}</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  const { successRate, failureRate } = calculateSuccessRate();
  const pieChartData = getPieChartData();
  const lineChartData = getLineChartData();

  return (
    <div className="overview-container">
      <div className="dashboard-grid">
        {/* Device Status Section */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Device Status</h2>
            <i className="fas fa-microchip card-icon"></i>
          </div>
          <div className="status-indicators">
            <div className="status-item status-connected">
              <div className="status-count">{formatNumber(overviewData.deviceStatus.connectedCount)}</div>
              <div className="status-label">Connected Devices</div>
            </div>
            <div className="status-item status-disconnected">
              <div className="status-count">{formatNumber(overviewData.deviceStatus.disconnectedCount)}</div>
              <div className="status-label">Disconnected Devices</div>
            </div>
          </div>
        </div>

        {/* Device Categories Section */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Device Categories</h2>
            <i className="fas fa-chart-pie card-icon"></i>
          </div>
          <div className="category-selector">
            {['oem', 'make', 'model', 'chipset'].map((category) => (
              <button
                key={category}
                className={`category-btn ${selectedCategory === category ? 'active' : ''}`}
                onClick={() => setSelectedCategory(category)}
              >
                {category.toUpperCase()}
              </button>
            ))}
          </div>
          <div className="chart-container">
            {pieChartData && <Pie data={pieChartData} options={pieChartOptions} />}
          </div>
          <div className="chart-legend">
            {pieChartData && pieChartData.labels.map((label, index) => {
              const total = pieChartData.datasets[0].data.reduce((acc, val) => acc + val, 0);
              const value = pieChartData.datasets[0].data[index];
              const percentage = Math.round((value / total) * 100);
              return (
                <div key={label} className="legend-item">
                  <div 
                    className="legend-color" 
                    style={{ backgroundColor: pieChartData.datasets[0].backgroundColor[index] }}
                  ></div>
                  <span>{label}: {value} ({percentage}%)</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Diagnostic Sessions Section */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Diagnostic Sessions</h2>
            <i className="fas fa-stethoscope card-icon"></i>
          </div>
          <div className="sessions-info">
            <div className="session-metric">
              <div className="session-count">{formatNumber(overviewData.remoteDiagnostics.total.active)}</div>
              <div className="status-label">Active Sessions</div>
            </div>
            <div className="session-metric">
              <div className="session-count">{formatNumber(overviewData.remoteDiagnostics.total.completed)}</div>
              <div className="status-label">Completed Sessions</div>
            </div>
          </div>
          <div className="user-sessions">
            <h3>Your Sessions</h3>
            <div className="sessions-info">
              <div className="session-metric">
                <div className="session-count">{overviewData.remoteDiagnostics.userSpecific.active}</div>
                <div className="status-label">Active</div>
              </div>
              <div className="session-metric">
                <div className="session-count">{overviewData.remoteDiagnostics.userSpecific.completed}</div>
                <div className="status-label">Completed</div>
              </div>
            </div>
          </div>
        </div>

        {/* Upload Statistics Section */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Upload Statistics</h2>
            <i className="fas fa-cloud-upload-alt card-icon"></i>
          </div>
          <div className="upload-metrics">
            <div className="upload-metric">
              <div className="upload-count success-rate">{successRate}%</div>
              <div className="status-label">Success Rate</div>
              <i className="fas fa-check-circle"></i>
            </div>
            <div className="upload-metric">
              <div className="upload-count failure-rate">{failureRate}%</div>
              <div className="status-label">Failure Rate</div>
              <i className="fas fa-times-circle"></i>
            </div>
          </div>
          
          {/* Content Statistics Table */}
          <div className="collector-stats-section">
            <h3 className="stats-table-title">Content Statistics</h3>
            <div className="stats-table-container">
              <table className="collector-stats-table">
                <thead>
                  <tr>
                    <th>Content</th>
                    <th>Total</th>
                    <th>Min</th>
                    <th>Max</th>
                    <th>Avg</th>
                    <th>Median</th>
                    <th>Standard Deviation</th>
                  </tr>
                </thead>
                <tbody>
                  {collectorStatsLoading ? (
                    <tr>
                      <td colSpan="7" style={{ textAlign: 'center', padding: '20px' }}>
                        Loading content statistics...
                      </td>
                    </tr>
                  ) : collectorStats && collectorStats.length > 0 ? (
                    collectorStats.map(collector => (
                      <tr key={collector.collectorId}>
                        <td className="collector-name">{collector.collectorName}</td>
                        <td>{collector.totalCount ?? 0}</td>
                        <td>{formatFileSize(collector.minSize)}</td>
                        <td>{formatFileSize(collector.maxSize)}</td>
                        <td>{formatFileSize(collector.avgSize)}</td>
                        <td>{formatFileSize(collector.medianSize)}</td>
                        <td>{formatFileSize(collector.stdDev)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" style={{ textAlign: 'center', padding: '20px' }}>
                        No content statistics available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          
          <div className="time-filter">
            <button
              className={timePeriod === 'week' ? 'active' : ''}
              onClick={() => setTimePeriod('week')}
            >
              Week
            </button>
            <button
              className={timePeriod === 'month' ? 'active' : ''}
              onClick={() => setTimePeriod('month')}
            >
              Month
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Overview;
